import scopeCSS from 'scope-css';
import * as xss from 'xss';
import { Recommendation } from '../../recommendation.types';
import { isSameSite } from '../../utility/metadata';
import { MessagePosition } from '../message-position.types';
import { xssHtmlOptions } from '../xss-options';

type PositionCoordinate = {
  x: number;
  y: number;
};
type isBeingClosed = boolean;

const closeButtonCssSelector = '[data-repro--close]';
const noCloseElementSelector = 'data-repro--no-close';

const createElementFromString = (rawString: string, shouldSanitizeHTML: boolean = false): HTMLElement => {
  const div = document.createElement('div');
  if (shouldSanitizeHTML) {
    const sanitizer = new xss.FilterXSS(xssHtmlOptions);
    div.innerHTML = sanitizer.process(rawString);
  } else {
    div.innerHTML = rawString;
  }

  return div.firstElementChild as HTMLElement;
};

export abstract class BaseMessageDOM {
  protected buildDomComplete: boolean;
  public el: HTMLElement;
  protected hideMessage: () => void;
  private shouldSanitizeHTML: boolean;
  private shouldRespectTargetInHTML: boolean;
  private temporarilyPreventCloseOnClick: boolean = false;

  protected position: MessagePosition;
  protected messageOffset: PositionCoordinate = {
    x: 0,
    y: 0,
  };

  protected targetOffset: PositionCoordinate = {
    x: 0,
    y: 0,
  };

  public constructor(
    params: {
      rawHtml: string;
      rawCss: string;
      scopedCss: boolean;
      scopedCssId: string;
      position: MessagePosition;
      shouldSanitizeHTML: boolean;
      shouldRespectTargetInHTML: boolean;
      recommendation?: Recommendation;
      elementShouldBeEmptyAndInvisible: boolean;
    },
    hideMessage: () => void
  ) {
    this.position = params.position;
    this.shouldSanitizeHTML = 'shouldSanitizeHTML' in params ? params.shouldSanitizeHTML : true;
    this.shouldRespectTargetInHTML = params.shouldRespectTargetInHTML;

    this.hideMessage = hideMessage;

    if (params.rawHtml === '' || params.elementShouldBeEmptyAndInvisible) {
      this.el = document.createElement('div');
      this.el.style.display = 'none';
      this.el.style.opacity = '0';
    } else {
      this.el = createElementFromString(params.rawHtml, this.shouldSanitizeHTML);
    }

    if (params.recommendation) {
      this.injectRecommendationIntoElement(this.el, params.recommendation);
    }

    const messageCss = createElementFromString(params.rawCss);

    if (messageCss && !params.elementShouldBeEmptyAndInvisible) {
      // TODO: パット見で長くて邪魔なので名前つけて private メソッドに括る
      if (params.scopedCss) {
        const { scopedCssId: id } = params;
        this.el.setAttribute(`data-repro-${id}`, '');
        const rawCss = messageCss.innerHTML;

        // Chrome 42以下ではclassNameが取得できないため
        // また、classListはcore-jsでpolyfillされないため
        const className = this.el.className || this.el.getAttribute('class') || '';
        const classNames = className.split(' ').filter((name) => !!name);

        try {
          messageCss.innerHTML = BaseMessageDOM.createScopedCSS({
            css: rawCss,
            elementId: this.el.getAttribute('id'),
            classNames,
            id,
          });
        } catch (e: any) {
          e.message = `${e.message} (${JSON.stringify({
            classListContents: this.el.classList,
            classListConstructor: this.el.classList ? this.el.classList.constructor.toString() : undefined,
          })})`;
          throw e;
        }
      }
      this.el.appendChild(messageCss);
    }

    this.adjustStyleTagText(); // TODO: Can do this with sanitizedCss directly
    this.buildDomComplete = true;
  }

  public close(): isBeingClosed {
    if (this.temporarilyPreventCloseOnClick) return false;
    if (this.el == null) return true;

    this.el.style.opacity = '0';

    setTimeout(() => {
      if (this.el && this.el.parentNode) {
        this.el.parentNode.removeChild(this.el);
      }
    }, 400);

    return true;
  }

  public static createScopedCSS({
    css,
    elementId,
    classNames,
    id,
  }: {
    css: string;
    elementId: string | null;
    classNames: string[];
    id: string;
  }): string {
    let rawCss = css;
    classNames.forEach((className) => {
      rawCss = rawCss.replace(new RegExp(`.${className}`, 'g'), ':host');
    });
    if (elementId) {
      rawCss = rawCss.replace(new RegExp(`#${elementId}`, 'g'), ':host');
    }
    rawCss = rawCss.replace(/\[data-repro--wrapper\]/g, ':host');
    rawCss = scopeCSS(rawCss, `[data-repro-${id}]`, {
      keyframes: true,
    });
    return rawCss.replace(/:host/g, '');
  }

  private injectRecommendationIntoElement(element: HTMLElement, recommendation: Recommendation): void {
    const { rqid, items } = recommendation;

    const recommendationItemElements = element.querySelectorAll(
      '.repro--silver-egg--item'
    ) as NodeListOf<HTMLAnchorElement>;

    recommendationItemElements.forEach((el, i) => {
      const item = items[i];

      el.dataset.silverEggCref = rqid;
      el.dataset.silverEggProd = item._id;
      el.href = item.ct_url;

      const imageElement = el.querySelector('.repro--silver-egg--item-image') as HTMLImageElement;
      imageElement.src = item.img_url;

      const titleElement = el.querySelector('.repro--silver-egg--item-name') as HTMLDivElement;
      titleElement.innerText = item.title;

      const priceElement = el.querySelector('.repro--silver-egg--item-price') as HTMLDivElement;
      priceElement.innerText = item.price_string;
    });
  }

  private get closeButtons(): NodeListOf<HTMLElement> | null {
    if (this.el == null) return null;
    return this.el.querySelectorAll(closeButtonCssSelector);
  }

  public attachCloseButtonEventListener(onCloseButtonClick: () => void): void {
    if (this.closeButtons === null || this.closeButtons.length === 0) return;
    this.closeButtons.forEach((closeButton: HTMLElement) => {
      closeButton.addEventListener('click', (e) => {
        e.preventDefault();
        onCloseButtonClick();
      });
    });
  }

  private ctaButtons(dataAttribute: string): NodeListOf<HTMLElement> | null {
    if (this.el == null) return null;
    return this.el.querySelectorAll(`${dataAttribute}`);
  }

  public attachCtaButtonEventListener(params: { dataAttribute: string; onButtonClick: () => void }): void {
    const buttons = this.ctaButtons(params.dataAttribute);
    if (buttons === null || buttons.length === 0) return;

    buttons.forEach((button: HTMLElement) => {
      // Web SDKの動作しているドメインとリンク先のドメインが一致するかをサーバーでは判定できないため
      // メッセージを表示するタイミングでリンク先をチェックしてtarget属性を書き換える
      if (button.tagName === 'A' && !this.shouldRespectTargetInHTML) {
        (button as HTMLAnchorElement).target = isSameSite((button as HTMLAnchorElement).href) ? '_self' : '_blank';
      }

      button.addEventListener('click', (el) => {
        // 被クリック要素に対する no-close 属性のチェックだけでなく、イベントリスナが貼られた要素もチェックする目的で
        // Event.target だけでなく Event.currentTarget も対象に属性値を確認している。
        // 詳細: https://github.com/reproio/web-sdk/pull/1116
        const shouldNotClose =
          (el.target as Element).getAttribute(noCloseElementSelector) != null ||
          (el.currentTarget as Element).getAttribute(noCloseElementSelector) != null ||
          (el.target as Element).querySelector(`[${noCloseElementSelector}]`) !== null ||
          (el.currentTarget as Element).querySelector(`[${noCloseElementSelector}]`) !== null;

        if (shouldNotClose) {
          this.temporarilyPreventCloseOnClick = true;
        }
        params.onButtonClick();
        this.temporarilyPreventCloseOnClick = false;
      });
    });
  }

  private adjustStyleTagText(): void {
    if (this.el == null) return;

    const styleTags = this.el.getElementsByTagName('style');
    if (styleTags.length > 0) {
      const originalStyleText = styleTags[0].textContent;
      styleTags[0].textContent = originalStyleText ? originalStyleText.replace(/&gt;/g, '>') : '';
    }
  }
}
